import type { OrderFormValues } from '@/components/santa-video/order-form-body'
import { BOOK_MIN_PERSONAL_DETAILS, minInterestsFor } from '@/server/domain/santa-video/book-order-form'
import { AGE_RANGE, MIN_PERSONAL_DETAILS } from '@/server/domain/santa-video/order-form'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

export type WizardStep = 0 | 1 | 2
export const LAST_STEP: WizardStep = 2
/** El identificador de cada paso: la clave de su copy y el `step` de la analítica. */
export const STEP_KEYS = ['child', 'knowledge', 'delivery'] as const
export type StepKey = (typeof STEP_KEYS)[number]
/** Por qué no se puede salir de un paso. La ausencia de error es `null` donde se pregunta, no un valor del tipo. */
export type StepError = 'name' | 'age' | 'details' | 'interests'
/** El `id` del email: lo pinta el campo y la analítica lo reconoce en el `invalid` del navegador. */
export const EMAIL_FIELD_ID = 'video-order-email'

/**
 * La edad es opcional; si viene, lo que acepta el servidor (`assertVideoOrderForm`):
 * un entero dentro de `AGE_RANGE`. Se comprueba en el paso 1 porque es donde
 * está el campo: si no, el 400 del servidor llegaba en el paso 3, dos pasos lejos.
 */
function isAcceptableAge(raw: string): boolean {
  const age = raw.trim()
  if (!age) return true
  const value = Number(age)
  return Number.isInteger(value) && value >= AGE_RANGE.min && value <= AGE_RANGE.max
}

function childStepError(values: OrderFormValues): StepError | null {
  if (!values.name.trim()) return 'name'
  return isAcceptableAge(values.age) ? null : 'age'
}

const filledCount = (entries: readonly string[]) => entries.filter((entry) => entry.trim()).length

function letterKnowledgeError(values: OrderFormValues): StepError | null {
  return filledCount(values.personalDetails) < MIN_PERSONAL_DETAILS ? 'details' : null
}

/** Lo que pide `assertBookOrderForm`: un hecho, y dos intereses sin reto o uno con él. */
function bookKnowledgeError(values: OrderFormValues): StepError | null {
  if (filledCount(values.personalDetails) < BOOK_MIN_PERSONAL_DETAILS) return 'details'
  const challenge = values.challenge.trim() || undefined
  return filledCount(values.interests) < minInterestsFor(challenge) ? 'interests' : null
}

const KNOWLEDGE_ERRORS: Record<VideoStoryId, (values: OrderFormValues) => StepError | null> = {
  letter: letterKnowledgeError,
  book: bookKnowledgeError,
}

/**
 * Lo mínimo para pasar de paso. NO es la validación del pedido —ésa es la del
 * servidor (el contrato de la historia)—: sólo evita que el padre llegue al
 * pago con un pedido que el servidor va a rechazar por lo obvio.
 */
export function validateStep(step: WizardStep, values: OrderFormValues, story: VideoStoryId): StepError | null {
  if (step === 0) return childStepError(values)
  if (step === 1) return KNOWLEDGE_ERRORS[story](values)
  return null
}
